/**
 * Background job dispatch.
 *
 * Browser code never talks to Trigger.dev directly. Every job is queued
 * through the `trigger-task` Edge Function, which validates the caller's
 * session, checks the staff role server-side and forwards the request to
 * Trigger.dev with the secret key. The function resolves who is calling, so
 * callers never send their own user id in the payload.
 *
 * Every failure path is turned into a `TriggerTaskError` with a message that
 * is safe to show inline. A missing or unreachable function (404, relay or
 * network error, 5xx) reads as "not deployed for this environment" so an
 * un-provisioned environment is never mistaken for a successful no-op.
 */
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';
import { supabase } from './supabase';

export const JOB_SERVICE_NOT_DEPLOYED =
  'Background job service is not deployed for this environment';

export type TriggerTaskId =
  | 'customer-provisioning-invite'
  | 'staff-invite'
  | 'zoho-pull-accounts'
  | 'zoho-pull-contacts'
  | 'zoho-learn-sync'
  | 'zoho-deal-trip-import'
  | 'auvik-sync-tenant-inventory'
  | 'sla-report-generate';

export type TriggerTaskErrorKind =
  | 'not_deployed'
  | 'not_allowed'
  | 'unauthorized'
  | 'forbidden'
  | 'rejected';

export class TriggerTaskError extends Error {
  readonly kind: TriggerTaskErrorKind;
  readonly status: number | null;
  readonly task: TriggerTaskId;

  constructor(task: TriggerTaskId, kind: TriggerTaskErrorKind, status: number | null, message: string) {
    super(message);
    this.name = 'TriggerTaskError';
    this.task = task;
    this.kind = kind;
    this.status = status;
  }
}

export interface TriggerTaskResult {
  /** Trigger.dev run id when the API returned one. */
  runId: string | null;
}

async function readBody(context: unknown): Promise<string> {
  if (context && typeof (context as Response).text === 'function') {
    try {
      return (await (context as Response).clone().text()).trim();
    } catch {
      return '';
    }
  }
  return '';
}

function statusOf(context: unknown): number | null {
  const status = (context as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

async function toTaskError(task: TriggerTaskId, error: unknown): Promise<TriggerTaskError> {
  if (error instanceof TriggerTaskError) return error;

  if (error instanceof FunctionsHttpError) {
    const status = statusOf(error.context);
    const body = await readBody(error.context);
    const lower = body.toLowerCase();

    if (status === 400 && lower.includes('task not allowed')) {
      return new TriggerTaskError(
        task,
        'not_allowed',
        status,
        `The background job service rejected this request: task "${task}" is not on its allowlist.`,
      );
    }
    if (status === 401) {
      return new TriggerTaskError(task, 'unauthorized', status, 'Your session has expired. Sign in again and retry.');
    }
    if (status === 403) {
      return new TriggerTaskError(
        task,
        'forbidden',
        status,
        'Your account is not recognised as PazNetworks staff by the background job service.',
      );
    }
    if (status === 404 || status === null || status >= 500) {
      const detail = body && body.length <= 120 ? ` (${body})` : '';
      return new TriggerTaskError(task, 'not_deployed', status, `${JOB_SERVICE_NOT_DEPLOYED}${detail}.`);
    }
    return new TriggerTaskError(
      task,
      'rejected',
      status,
      body ? `The background job service returned ${status}: ${body.slice(0, 200)}` : `The background job service returned ${status}.`,
    );
  }

  if (error instanceof FunctionsRelayError || error instanceof FunctionsFetchError) {
    return new TriggerTaskError(task, 'not_deployed', null, `${JOB_SERVICE_NOT_DEPLOYED}.`);
  }

  const message = error instanceof Error ? error.message : String(error);
  return new TriggerTaskError(task, 'rejected', null, message || 'The background job could not be queued.');
}

/**
 * Queue a Trigger.dev task through the `trigger-task` Edge Function.
 * Resolves with the run id on 2xx; rejects with a `TriggerTaskError` otherwise.
 */
export async function invokeTriggerTask(
  task: TriggerTaskId,
  payload: Record<string, unknown> = {},
  opts: { idempotencyKey?: string } = {},
): Promise<TriggerTaskResult> {
  let response: { data: unknown; error: unknown };
  try {
    response = await supabase.functions.invoke('trigger-task', {
      body: {
        task,
        payload,
        ...(opts.idempotencyKey ? { idempotencyKey: opts.idempotencyKey } : {}),
      },
    });
  } catch (error) {
    throw await toTaskError(task, error);
  }

  if (response.error) {
    throw await toTaskError(task, response.error);
  }

  const data = response.data as { id?: unknown; ok?: unknown; error?: unknown } | null;
  if (data && data.ok === false) {
    const detail = typeof data.error === 'string' ? data.error : 'the background job service rejected the request';
    throw new TriggerTaskError(task, 'rejected', 200, `Could not queue the job: ${detail}.`);
  }

  return { runId: data && typeof data.id === 'string' ? data.id : null };
}

/** Human-readable message for any error thrown by a job mutation. */
export function describeJobError(error: unknown): string {
  if (error instanceof TriggerTaskError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'The background job could not be queued.';
}
